import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { SelectField } from '@wk/components-v3-react16';
import { useDurationFilter } from './useDurationFilter';
import type { DurationFilterModel, FilterType } from './types';
import styles from './DurationFilter.module.scss';

interface DurationFilterProperties
  extends CustomFilterDisplayProps<DurationFilterModel>,
    FilterWrapperParams {}

const parseMinutesFromModel = (minutes?: number): string => {
  return minutes === undefined ? '' : minutes.toString();
};

const DurationFilter = ({
  model,
  onStateChange,
  state,
}: DurationFilterProperties) => {
  const componentReference = useRef<HTMLDivElement>(null);
  const initialModelSet = useRef(false);

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        componentReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useDurationFilter(model);

  const defaultFilterType: FilterType = 'greaterThan';

  const [filterType, setFilterType] = useState<FilterType>(
    state.model?.type || defaultFilterType
  );
  const [fromMinutes, setFromMinutes] = useState<string>(
    parseMinutesFromModel(state.model?.fromMinutes)
  );
  const [toMinutes, setToMinutes] = useState<string>(
    parseMinutesFromModel(state.model?.toMinutes)
  );

  // Set initial model when filter opens (so Apply works even without interaction)
  useEffect(() => {
    if (!state.model && !initialModelSet.current) {
      const fromValue = Number.parseInt(fromMinutes, 10);
      if (!Number.isNaN(fromValue) && fromValue >= 0) {
        const initialModel: DurationFilterModel = {
          fromMinutes: fromValue,
          type: filterType,
        };
        onStateChange({ model: initialModel });
        initialModelSet.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state when model changes (for Reset button)
  useEffect(() => {
    if (state.model) {
      setFilterType(state.model.type);
      setFromMinutes(parseMinutesFromModel(state.model.fromMinutes));
      setToMinutes(parseMinutesFromModel(state.model.toMinutes));
    } else {
      setFilterType(defaultFilterType);
      setFromMinutes('');
      setToMinutes('');
    }
  }, [state.model]);

  const filterTypeOptions = useMemo(
    () => [
      { label: 'Greater Than', value: 'greaterThan' },
      { label: 'Less Than', value: 'lessThan' },
      { label: 'Between', value: 'inRange' },
    ],
    []
  );

  const handleFilterTypeChange = (newType: FilterType) => {
    setFilterType(newType);

    const fromValue = Number.parseInt(fromMinutes, 10);
    if (Number.isNaN(fromValue) || fromValue < 0) {
      return;
    }

    const newModel: DurationFilterModel = {
      fromMinutes: fromValue,
      type: newType,
    };

    if (newType === 'inRange') {
      const toValue = Number.parseInt(toMinutes, 10);
      if (!Number.isNaN(toValue) && toValue >= 0) {
        newModel.toMinutes = toValue;
      }
    }

    onStateChange({ model: newModel });
  };

  const handleFromMinutesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const isValidNumericInput = value === '' || /^\d+$/.test(value);

      if (!isValidNumericInput) {
        return;
      }

      setFromMinutes(value);

      if (value === '') {
        onStateChange({ model: null });
        return;
      }

      const fromValue = Number.parseInt(value, 10);
      if (Number.isNaN(fromValue) || fromValue < 0) {
        return;
      }

      const newModel: DurationFilterModel = {
        fromMinutes: fromValue,
        type: filterType,
      };

      if (filterType === 'inRange') {
        const toValue = Number.parseInt(toMinutes, 10);
        if (!Number.isNaN(toValue) && toValue >= 0) {
          newModel.toMinutes = toValue;
        }
      }

      onStateChange({ model: newModel });
    },
    [filterType, toMinutes, onStateChange]
  );

  const handleToMinutesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const isValidNumericInput = value === '' || /^\d+$/.test(value);

      if (!isValidNumericInput) {
        return;
      }

      setToMinutes(value);

      const fromValue = Number.parseInt(fromMinutes, 10);
      if (Number.isNaN(fromValue) || fromValue < 0) {
        return;
      }

      const newModel: DurationFilterModel = {
        fromMinutes: fromValue,
        type: filterType,
      };

      if (value !== '') {
        const toValue = Number.parseInt(value, 10);
        if (!Number.isNaN(toValue) && toValue >= 0) {
          newModel.toMinutes = toValue;
        }
      }

      onStateChange({ model: newModel });
    },
    [filterType, fromMinutes, onStateChange]
  );

  const getPlaceholder = (isFrom: boolean): string => {
    if (filterType === 'inRange') {
      return isFrom ? 'Enter from # minutes' : 'Enter to # minutes';
    }
    return 'Enter # minutes';
  };

  return (
    <div className={styles.container} ref={componentReference} tabIndex={-1}>
      <div className={styles.filterContent}>
        <div className={styles.filterRow}>
          <div className={styles.filterSelect}>
            <SelectField size="medium">
              <select
                value={filterType}
                onChange={(event) =>
                  handleFilterTypeChange(event.target.value as FilterType)
                }
              >
                {filterTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </SelectField>
          </div>
        </div>

        <div className={styles.inputSection}>
          <div className={styles.inputRow}>
            <div className={styles.minutesInput}>
              <input
                type="text"
                value={fromMinutes}
                onChange={handleFromMinutesChange}
                placeholder={getPlaceholder(true)}
              />
            </div>
          </div>
        </div>

        {filterType === 'inRange' && (
          <div className={styles.inputSection}>
            <div className={styles.inputRow}>
              <div className={styles.minutesInput}>
                <input
                  type="text"
                  value={toMinutes}
                  onChange={handleToMinutesChange}
                  placeholder={getPlaceholder(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DurationFilter;
